import React, {useEffect, useMemo} from 'react';
import Chart from "react-apexcharts";
import {ApexOptions} from "apexcharts";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {ADMIN_LANG} from "../../../settings";
import {Col, Container, InputGroup, Row, Table, Form} from "react-bootstrap";
import {clearError, getAdminDashboard} from "../../../app/slices/adminDashboard";
import AdminLoader from "../AdminLoader";
import toast from "react-hot-toast";
import {formatDate} from "../utils";
import {DataParams} from "../../../types/admin";
import DatePicker from "react-datepicker";
import {format, subDays} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import {useSearchParamHandler} from "./ListFilters";
import {useSearchParams} from "react-router-dom";
import EventTable from "../EventTable";
import {ChevronRight} from "react-bootstrap-icons";

type Props = {
    params: DataParams;
};

const AdminDashboard = ({params}: Props) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const dispatch = useAppDispatch();
    const {
        loaded,
        error,
        statistics,
        game_sessions,
        events,
        most_popular_games,
        date_start,
        date_end,
        date_range,
    } = useAppSelector((state) => state.adminDashboard);

    const today = new Date();
    const defaultStartDate = subDays(today, 30);

    const {currentValue: dateStart, handleParamChange: setDateStart} = useSearchParamHandler('date_start');
    const {currentValue: dateEnd, handleParamChange: setDateEnd} = useSearchParamHandler('date_end');

    const startDate = dateStart ? new Date(dateStart) : defaultStartDate;
    const endDate = dateEnd ? new Date(dateEnd) : today;

    useEffect(() => {
        // Fetch dashboard data when the component mounts or dates change
        dispatch(getAdminDashboard({...params}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    const handleStartDateChange = (date: Date | null) => {
        if (date) {
            if (endDate && date > endDate) {
                setDateEnd(format(date, 'yyyy-MM-dd')); // Update endDate if it's before the new startDate
            }

            setDateStart(format(date, 'yyyy-MM-dd'));

            setSearchParams({
                date_start: format(date, 'yyyy-MM-dd'),
                date_end: format(endDate || today, 'yyyy-MM-dd') // Default endDate if it's null
            });
        }
    };

    const handleEndDateChange = (date: Date | null) => {
        if (date) {
            if (startDate && date < startDate) {
                setDateStart(format(date, 'yyyy-MM-dd')); // Update startDate if it's after the new endDate
            }

            setDateEnd(format(date, 'yyyy-MM-dd'));

            setSearchParams({
                date_start: format(startDate || defaultStartDate, 'yyyy-MM-dd'), // Default startDate if it's null
                date_end: format(date, 'yyyy-MM-dd')
            });
        }
    };

    const parseFinnishDate = (dateString: string): number => {
        const [day, month, year] = dateString.split('.');
        return new Date(`${year}-${month}-${day}`).getTime();
    };

    const options: ApexOptions = useMemo(() => ({
        chart: {
            type: 'line',
            height: 350,
            toolbar: {
                show: false
            }
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        xaxis: {
            type: 'datetime',
            categories: statistics?.graph?.labels.map(parseFinnishDate) || [],
            labels: {
                format: 'dd.MM.yyyy',
            },
        },
        yaxis: {
            title: {
                text: ''
            }
        },
        dataLabels: {
            enabled: false
        },
        markers: {
            size: 4,
            strokeWidth: 2,
        },
        tooltip: {
            enabled: true,
            shared: false,
            intersect: false,
            x: {
                format: 'dd.MM.yyyy',
            },
        }
    }), [statistics?.graph?.labels]);

    const series: ApexAxisChartSeries = useMemo(() => ([
        {
            name: ADMIN_LANG.games_played,
            data: statistics?.graph?.data || []
        }
    ]), [statistics?.graph?.data]);

    const sessionRows = useMemo(() => game_sessions.map((session) => {
        const startDate = session.date_start ? formatDate(session.date_start) : null;
        const gameUrl = `/admin/games/${session.game_id}/versions/${session.game_version_id}`;
        return (
            <tr key={session.key}>
                <td><a href={gameUrl}>{session.game}</a></td>
                <td>{session.name}</td>
                <td className='text-center'>{session.page} / {session.pages}</td>
                <td className='text-end'>{startDate}</td>
            </tr>
        );
    }), [game_sessions]);

    const gameRows = useMemo(() => most_popular_games?.map((game) => {
        const gameUrl = `/admin/games/${game.game_id}/versions/${game.version_id}`;
        return (
            <tr key={game.key}>
                <td><a href={gameUrl}>{game.name}</a></td>
                <td>{game.played}</td>
                <td>{formatDate(game.created)}</td>
            </tr>
        );
    }), [most_popular_games]);

    const comparisonPercentage = useMemo(() => {
        if (statistics?.comparison === undefined || statistics.comparison === null) {
            return '∞';
        }
        return (statistics.comparison * 100).toFixed(1);
    }, [statistics?.comparison]);


    const comparisonClass = useMemo(() => {
        return parseFloat(comparisonPercentage) < 0 ? 'chevron negative' : 'chevron positive';
    }, [comparisonPercentage]);

    if (error) {
        toast.error(error, {position: 'top-right',});
        dispatch(clearError());
    }

    const timeChanged = date_start || date_end;

    return (
        <Container>
            {!loaded && <AdminLoader/>}
            <Row className='pt-4 pb-4'>
                <Col xs={12}>
                    <div className='admin-card'>
                        <div className='d-lg-flex align-items-center justify-content-lg-between pb-3'>
                            <div className='pb-3 pb-lg-0'>
                                {timeChanged ? (
                                    <h2>
                                        {ADMIN_LANG.games_played}:
                                        <span className='timespan-statistic'>
                                            {statistics?.interval_played}
                                        </span>
                                        <> </>
                                        <span className='timespan-info'>
                                            ({date_range}&nbsp;{ADMIN_LANG.days_abbreviation}, {date_start} - {date_end})
                                        </span>
                                    </h2>
                                ) : (
                                    <h2>
                                        {ADMIN_LANG.games_played}:
                                        <span className='timespan-statistic'>{statistics?.interval_played}</span>
                                        <> </>
                                        <span className='timespan-info'>
                                            ({date_range}&nbsp;{ADMIN_LANG.days_abbreviation})
                                        </span>

                                    </h2>
                                )}
                                <p>{ADMIN_LANG.compared_to_previous}
                                    &nbsp;<span
                                        className={comparisonClass}><ChevronRight/> {comparisonPercentage} %</span>
                                </p>
                            </div>
                            <div className="d-flex flex-column align-items-start">
                                <Form.Label>{ADMIN_LANG.date_range}</Form.Label>
                                <div className='d-flex'>
                                    <InputGroup>
                                        <DatePicker
                                            selected={startDate}
                                            onChange={handleStartDateChange}
                                            startDate={startDate}
                                            selectsStart
                                            endDate={endDate}
                                            maxDate={today}
                                            dateFormat="dd.MM.yyyy"
                                            className="date-picker"
                                        />
                                    </InputGroup>
                                    <InputGroup className='ms-2'>
                                        <DatePicker
                                            selected={endDate}
                                            onChange={handleEndDateChange}
                                            startDate={startDate}
                                            endDate={endDate}
                                            maxDate={today}
                                            selectsEnd
                                            minDate={startDate || undefined}
                                            dateFormat="dd.MM.yyyy"
                                            className="date-picker"
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                        </div>
                        <hr className='pb-3'></hr>
                        {statistics && <Chart options={options} series={series} type="line" height={350}/>}
                    </div>
                </Col>
            </Row>
            <Row className='gy-3 pb-4'>
                <Col xs={6} md={4} xxl={2}>
                    <div className='admin-card statistic'>
                        <h3>{ADMIN_LANG.games}</h3>
                        <p>{statistics?.games}</p>
                    </div>
                </Col>
                <Col xs={6} md={4} xxl={2}>
                    <div className='admin-card statistic'>
                        <h3>{ADMIN_LANG.games_played}</h3>
                        <p>{statistics?.games_played}</p>
                    </div>
                </Col>
                <Col xs={6} md={4} xxl={2}>
                    <div className='admin-card statistic'>
                        <h3>{ADMIN_LANG.questions}</h3>
                        <p>{statistics?.questions}</p>
                    </div>
                </Col>
                <Col xs={6} md={4} xxl={2}>
                    <div className='admin-card statistic'>
                        <h3>{ADMIN_LANG.questions_played}</h3>
                        <p>{statistics?.questions_played}</p>
                    </div>
                </Col>
                <Col xs={6} md={4} xxl={2}>
                    <div className='admin-card statistic'>
                        <h3>{ADMIN_LANG.users}</h3>
                        <p>{statistics?.users}</p>
                    </div>
                </Col>
                <Col xs={6} md={4} xxl={2}>
                    <div className='admin-card statistic'>
                        <h3>{ADMIN_LANG.teams}</h3>
                        <p>{statistics?.teams}</p>
                    </div>
                </Col>
            </Row>
            <Row className='pb-4 gy-4'>
                <Col xs={12} md={6}>
                    <div className='admin-card'>
                        <h3>{ADMIN_LANG.latest_sessions}</h3>
                        <Table responsive hover striped>
                            <thead>
                            <tr>
                                <th>{ADMIN_LANG.game_name}</th>
                                <th>{ADMIN_LANG.player_name}</th>
                                <th className='text-center'>{ADMIN_LANG.pages}</th>
                                <th className='text-end'>{ADMIN_LANG.started}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sessionRows}
                            </tbody>
                        </Table>
                    </div>
                </Col>
                <Col xs={12} md={6}>
                    <div className='admin-card'>
                        <h3>{ADMIN_LANG.most_popular_games}</h3>
                        <Table responsive hover striped>
                            <thead>
                            <tr>
                                <th>{ADMIN_LANG.name}</th>
                                <th>{ADMIN_LANG.played}</th>
                                <th>{ADMIN_LANG.created}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {gameRows}
                            </tbody>
                        </Table>
                    </div>
                </Col>
                <Col xs={12}>
                    <div className='admin-card'>
                        <h3>{ADMIN_LANG.latest_events}</h3>
                        <EventTable events={events}/>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default AdminDashboard;